import React, { createContext, useContext, useState } from 'react';
import { getDeliveryOptionsForAddress, calculateDeliveryCost } from '../utils/deliveryUtils';

// Create the checkout context
const CheckoutContext = createContext();

// Custom hook to use the checkout context
export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
};

// Checkout provider component
export const CheckoutProvider = ({ children }) => {
  // State for checkout data
  const [checkoutData, setCheckoutData] = useState({
    productId: null,
    productName: null,
    selectedSize: null,
    price: null,
    shippingAddress: null,
    shipping: null, // Legacy field for compatibility
    deliveryMethod: null, // New delivery system
    deliveryCost: 0,
    availableDeliveryOptions: [],
    customerEmail: null,
    customerPhone: null,
  });

  // Update checkout data
  const updateCheckoutData = (newData) => {
    setCheckoutData(prevData => ({
      ...prevData,
      ...newData
    }));
  };

  // Clear checkout data
  const clearCheckoutData = () => {
    setCheckoutData({
      productId: null,
      productName: null,
      selectedSize: null,
      price: null,
      shippingAddress: null,
      shipping: null,
      deliveryMethod: null,
      deliveryCost: 0,
      availableDeliveryOptions: [],
      customerEmail: null,
      customerPhone: null,
    });
  };

  // Update available delivery options based on shipping address
  const updateDeliveryOptions = (shippingAddress) => {
    if (!shippingAddress) return;

    const availableOptions = getDeliveryOptionsForAddress(shippingAddress);
    updateCheckoutData({
      availableDeliveryOptions: availableOptions,
      // Reset delivery selection when address changes
      deliveryMethod: null,
      deliveryCost: 0
    });
  };

  // Select delivery method and calculate cost
  const selectDeliveryMethod = (deliveryOptionId, weightKg = 0.5) => {
    const cost = calculateDeliveryCost(deliveryOptionId, weightKg);
    
    updateCheckoutData({
      deliveryMethod: deliveryOptionId,
      deliveryCost: cost,
      // Update legacy shipping field for backward compatibility
      shipping: {
        method: deliveryOptionId,
        cost: cost,
        estimatedDays: checkoutData.availableDeliveryOptions.find(
          opt => opt.id === deliveryOptionId
        )?.estimatedDays || 'Unknown'
      }
    });
  };

  // Get total price including delivery
  const getTotalPrice = () => {
    const basePrice = checkoutData.price || 0;
    const deliveryCost = checkoutData.deliveryCost || 0;
    return basePrice + deliveryCost;
  };

  // Get selected delivery option details
  const getSelectedDeliveryOption = () => {
    if (!checkoutData.deliveryMethod || !checkoutData.availableDeliveryOptions.length) {
      return null;
    }
    
    return checkoutData.availableDeliveryOptions.find(
      option => option.id === checkoutData.deliveryMethod
    );
  };

  // Check if we have the necessary data for a specific checkout step
  const hasRequiredDataForStep = (step) => {
    switch (step) {
      case 'confirmation':
        return !!checkoutData.productId && !!checkoutData.price;
      case 'shipping':
        return !!checkoutData.productId && !!checkoutData.price;
      case 'shipping-options':
        return !!checkoutData.productId && !!checkoutData.price && !!checkoutData.shippingAddress;
      case 'payment':
        return !!checkoutData.productId && !!checkoutData.price && !!checkoutData.shippingAddress && !!checkoutData.shipping;
      default:
        return false;
    }
  };

  // Provide the context value
  const value = {
    checkoutData,
    updateCheckoutData,
    clearCheckoutData,
    hasRequiredDataForStep,
    updateDeliveryOptions,
    selectDeliveryMethod,
    getTotalPrice,
    getSelectedDeliveryOption
  };

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};
